import {z} from 'zod';
import type {AccountsSwotStatus} from '@api/generated/client';
import {
  accountsSubStageSchema,
  DEFAULT_SUB_STAGE_LABELS,
  findPositionBySystemSubStage,
  type AccountsSubStageKey,
  type AccountsViewConfig,
} from '@common/accountsView';
import {formatDate} from '@common/lib/utils';
import type {RouterOutput} from '@common-web/lib/trpc';

type AccountActivityEvent = RouterOutput['account']['getActivity'][number];

const SWOT_STATUS_PHRASE: Record<AccountsSwotStatus, string> = {
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  REQUESTED: 'requested',
  SCHEDULED: 'scheduled',
  SKIP_REQUESTED: 'skip requested',
  SKIPPED: 'skipped',
};

const swotStatusSchema = z.custom<AccountsSwotStatus>(
  (value) => typeof value === 'string' && value in SWOT_STATUS_PHRASE,
);

const stageChangeSchema = z
  .object({
    newSubStage: accountsSubStageSchema,
    rescheduled: z.boolean(),
    subStage: accountsSubStageSchema,
    swotScheduledDate: z.string().nullable(),
    swotStatus: swotStatusSchema.nullable(),
  })
  .partial();

type StageChange = z.infer<typeof stageChangeSchema>;

function parseStageChange(data: AccountActivityEvent['newData']): StageChange {
  const parsed = stageChangeSchema.safeParse(data);
  return parsed.success ? parsed.data : {};
}

function describeSwotStatus(
  swotStatus: AccountsSwotStatus,
  {rescheduled, swotScheduledDate}: StageChange,
): string {
  if (swotStatus !== 'SCHEDULED' || !swotScheduledDate) return SWOT_STATUS_PHRASE[swotStatus];
  const date = formatDate(swotScheduledDate, 'MMM D, YYYY');
  return `${rescheduled ? 'rescheduled' : 'scheduled'} for ${date}`;
}

function describeSwotChange(
  before: StageChange,
  after: StageChange,
  callLabel: string,
): string | undefined {
  if (after.swotStatus) return `${callLabel} ${describeSwotStatus(after.swotStatus, after)}`;
  // A manual move back to the SWOT Call step records the cleared status as null.
  if (after.swotStatus === null && before.swotStatus) {
    return `${callLabel} reset (was ${describeSwotStatus(before.swotStatus, before)})`;
  }
  return undefined;
}

/** The stage move and SWOT call change an activity row records, e.g.
 *  "Prospect → SWOT Call · SWOT Call scheduled for Nov 3, 2026". */
export function describeAccountActivity(
  {newData, oldData}: Pick<AccountActivityEvent, 'newData' | 'oldData'>,
  config: AccountsViewConfig,
): string | undefined {
  const before = parseStageChange(oldData);
  const after = parseStageChange(newData);
  const labelSubStage = (subStage: AccountsSubStageKey) =>
    findPositionBySystemSubStage(config, subStage)?.subStage.label ??
    DEFAULT_SUB_STAGE_LABELS[subStage];

  // CBS_STAGE_ADVANCED rows record their destination as newSubStage.
  const toSubStage = after.subStage ?? after.newSubStage;
  const parts: string[] = [];
  if (before.subStage && toSubStage && before.subStage !== toSubStage) {
    parts.push(`${labelSubStage(before.subStage)} → ${labelSubStage(toSubStage)}`);
  } else if (!before.subStage && after.newSubStage) {
    parts.push(`Moved to ${labelSubStage(after.newSubStage)}`);
  }
  const swotChange = describeSwotChange(before, after, labelSubStage('SWOT_CALL'));
  if (swotChange) parts.push(swotChange);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
